/**
 * Dashboard Page
 * Requirements: 3.5
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { signOut } from 'aws-amplify/auth';
import { api } from '../services/api';
import { Header } from '../components/Header';
import { Sidebar } from '../components/Sidebar';
import { JobCard } from '../components/JobCard';
import { AddJobModal } from '../components/AddJobModal';
import { AddJobFromURL } from '../components/AddJobFromURL';
import { PreferencesModal } from '../components/PreferencesModal';
import { SubcomponentEditor } from '../components/SubcomponentEditor';
import { ResumeEditor } from '../components/ResumeEditor';
import { JobDescriptionEditor } from '../components/JobDescriptionEditor';
import { TutorialModal } from '../components/TutorialModal';
import { TutorialWalkthrough } from '../components/TutorialWalkthrough';
import type { UserJob, JobPhase, SubcomponentType, UserPreferences } from '../types';
import { KanbanBoard } from '../components/KanbanBoard';
import { Loader2, Inbox, LayoutGrid, Columns, CheckCircle, Info, X, Menu } from 'lucide-react';
import useJobPolling from '../hooks/useJobPolling';
import useTutorial from '../hooks/useTutorial';
import useCheckoutNotification from '../hooks/useCheckoutNotification';
import useAutoFinalGeneration from '../hooks/useAutoFinalGeneration';

export function Dashboard() {
  const [jobs, setJobs] = useState<UserJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedResume, setSelectedResume] = useState<string | null>(null);
  const [selectedPhase, setSelectedPhase] = useState<JobPhase | 'active' | 'all'>('active');
  const [phaseCounts, setPhaseCounts] = useState<Record<JobPhase, number>>({} as Record<JobPhase, number>);
  const [searchQuery, setSearchQuery] = useState('');
  const [userPreferences, setUserPreferences] = useState<UserPreferences | undefined>(undefined);

  // Modals
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [showAddJobUrlModal, setShowAddJobUrlModal] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [showResumeEditor, setShowResumeEditor] = useState(false);
  const [editingResumeName, setEditingResumeName] = useState<string | undefined>(undefined);
  const [editingSubcomponent, setEditingSubcomponent] = useState<{
    jobid: string;
    component: SubcomponentType;
    content: string;
  } | null>(null);
  const [editingJob, setEditingJob] = useState<{
    jobid: string;
    jobdesc: string;
    jobtags: string[];
    tagsGenerating?: boolean;
  } | null>(null);
  // View mode: grid (default) or kanban
  const [viewMode, setViewMode] = useState<'grid' | 'kanban'>('grid');

  // Mobile sidebar state
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Checkout notification hook
  const { checkoutNotification, setCheckoutNotification } = useCheckoutNotification({ setUserPreferences });

  // Resume refresh key: bump to trigger Header resume list reload without full page refresh
  const [resumeRefreshKey, setResumeRefreshKey] = useState(0);

  // Track whether initial phase selection has been applied
  const initialPhaseApplied = useRef(false);

  const loadJobs = useCallback(async (isBackgroundRefresh = false) => {
    // Only show loading spinner on initial load or explicit refresh, not during background polling
    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    setError(null);
    try {
      let response;
      // Kanban mode always loads all jobs (needs every phase for columns)
      if (viewMode === 'kanban') {
        response = await api.listJobs(undefined, 'all');
      } else if (selectedPhase === 'active') {
        response = await api.listJobs(undefined, 'active');
      } else if (selectedPhase === 'all') {
        response = await api.listJobs(undefined, 'all');
      } else {
        response = await api.listJobs(selectedPhase);
      }
      setJobs(response.jobs || []);
      setPhaseCounts(response.phaseCounts || {});
    } catch (err: any) {
      console.error('Failed to load jobs:', err);
      setError(err?.message || 'Failed to load jobs');
      setJobs([]);
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    }
  }, [selectedPhase, viewMode]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Request 3: Dynamic default phase — after initial load, pick Queued if any exist, else Ready
  useEffect(() => {
    if (initialPhaseApplied.current) return;
    if (loading) return; // Wait until first load completes
    if (Object.keys(phaseCounts).length === 0 && jobs.length === 0) return; // No data yet

    initialPhaseApplied.current = true;
    if ((phaseCounts['Queued'] || 0) > 0) {
      setSelectedPhase('Queued');
    } else {
      setSelectedPhase('Ready');
    }
  }, [loading, phaseCounts, jobs.length]);

  // Load user preferences on mount
  useEffect(() => {
    async function loadPreferences() {
      try {
        const prefs = await api.getPreferences();
        setUserPreferences(prefs);
      } catch (err: any) {
        console.error('Failed to load user preferences:', err);
        const status = err?.response?.status;
        // 403 on preferences means user exists in Cognito but not in backend DB.
        // Clear auth state and redirect to login.
        if (status === 403) {
          console.warn('User not found in backend (403). Clearing auth state.');
          try {
            await signOut();
          } catch {
            localStorage.clear();
            sessionStorage.clear();
          }
          window.location.href = '/';
        }
      }
    }
    loadPreferences();
  }, []);

  // Job polling (generation tracking, 2s active polling, 30s background polling)
  useJobPolling({ jobs, loadJobs });

  // Filter jobs based on search query
  const filteredJobs = useMemo(() => {
    if (!searchQuery.trim()) return jobs;
    const query = searchQuery.toLowerCase();
    return jobs.filter((job) =>
      job.jobtitle.toLowerCase().includes(query) ||
      job.jobcompany.toLowerCase().includes(query) ||
      (job.joblocation && job.joblocation.toLowerCase().includes(query)) ||
      (job.jobtags && job.jobtags.some(tag => tag.toLowerCase().includes(query)))
    );
  }, [jobs, searchQuery]);

  // Tutorial hook
  const {
    showTutorialModal,
    showWalkthrough,
    setShowTutorialModal,
    handleTutorialComplete,
    handleReplayWalkthrough,
    handleWalkthroughComplete,
  } = useTutorial({ userPreferences, setUserPreferences });

  // Auto-final-generation hook
  const { handleGenerateResume } = useAutoFinalGeneration({ jobs, loadJobs });

  // Optimistic job update: replace a single job in the jobs array without re-fetching
  const handleJobUpdate = useCallback((updatedJob: UserJob) => {
    setJobs(prev => prev.map(j =>
      j.jobid === updatedJob.jobid ? updatedJob : j
    ));
  }, []);

  // Optimistic phase change for kanban drag-and-drop (no API reload)
  const handleOptimisticPhaseChange = useCallback((jobid: string, newPhase: JobPhase) => {
    setJobs(prev => prev.map(j =>
      j.jobid === jobid ? { ...j, jobphase: newPhase } : j
    ));
    setPhaseCounts(prev => {
      const oldPhase = jobs.find(j => j.jobid === jobid)?.jobphase;
      if (!oldPhase || oldPhase === newPhase) return prev;
      return {
        ...prev,
        [oldPhase]: Math.max(0, (prev[oldPhase] || 0) - 1),
        [newPhase]: (prev[newPhase] || 0) + 1,
      };
    });
  }, [jobs]);

  function handleEditSubcomponent(jobid: string, component: SubcomponentType) {
    const job = jobs.find((j) => j.jobid === jobid);
    if (job) {
      const content = job[`data${component}` as keyof UserJob] as string || '';
      setEditingSubcomponent({ jobid, component, content });
    }
  }

  function handleEditJob(jobid: string) {
    const job = jobs.find((j) => j.jobid === jobid);
    if (job) {
      const currentTags = job.jobtags || [];
      setEditingJob({ jobid, jobdesc: job.jobdesc, jobtags: currentTags });

      // Auto-regenerate tags if the job has fewer than 5
      if (currentTags.length < 5 && job.jobdesc) {
        setEditingJob({ jobid, jobdesc: job.jobdesc, jobtags: currentTags, tagsGenerating: true });

        // Trigger tag generation by re-saving the job description (backend fires async gen_tags Lambda)
        api.updateJobDescription(jobid, job.jobdesc).catch((err) => {
          console.warn('Background tag generation trigger failed:', err);
        });

        // Poll for updated tags (gen_tags Lambda takes ~3-5s)
        let attempts = 0;
        const maxAttempts = 5;
        const pollInterval = 3000;
        const tagPollTimer = setInterval(async () => {
          attempts++;
          try {
            const refreshedJob = await api.getJob(jobid);
            const newTags = refreshedJob.jobtags || [];
            if (newTags.length > currentTags.length || attempts >= maxAttempts) {
              clearInterval(tagPollTimer);
              if (newTags.length > currentTags.length) {
                // Update editingJob state so JobDescriptionEditor picks up new tags
                setEditingJob((prev) =>
                  prev && prev.jobid === jobid
                    ? { ...prev, jobtags: newTags, tagsGenerating: false }
                    : prev
                );
                // Also update the job in the main jobs list
                handleJobUpdate(refreshedJob);
              } else {
                // Max attempts reached without new tags — stop the indicator
                setEditingJob((prev) =>
                  prev && prev.jobid === jobid
                    ? { ...prev, tagsGenerating: false }
                    : prev
                );
              }
            }
          } catch (err) {
            console.warn('Tag poll failed:', err);
            clearInterval(tagPollTimer);
            setEditingJob((prev) =>
              prev && prev.jobid === jobid
                ? { ...prev, tagsGenerating: false }
                : prev
            );
          }
        }, pollInterval);
      }
    }
  }

  function handleRegenerateTags() {
    if (!editingJob) return;
    const { jobid, jobdesc } = editingJob;

    // Clear tags in UI immediately and show generating indicator
    setEditingJob(prev => prev ? { ...prev, jobtags: [], tagsGenerating: true } : prev);

    // Clear existing tags via API, then trigger regeneration
    api.updateJobTags(jobid, []).then(() => {
      return api.updateJobDescription(jobid, jobdesc);
    }).catch(err => {
      console.warn('Regenerate tags trigger failed:', err);
    });

    // Poll for updated tags
    let attempts = 0;
    const maxAttempts = 7;
    const pollInterval = 3000;
    const tagPollTimer = setInterval(async () => {
      attempts++;
      try {
        const refreshedJob = await api.getJob(jobid);
        const newTags = refreshedJob.jobtags || [];
        if (newTags.length > 0 || attempts >= maxAttempts) {
          clearInterval(tagPollTimer);
          setEditingJob(prev =>
            prev && prev.jobid === jobid
              ? { ...prev, jobtags: newTags, tagsGenerating: false }
              : prev
          );
          if (newTags.length > 0) {
            handleJobUpdate(refreshedJob);
          }
        }
      } catch (err) {
        console.warn('Tag regen poll failed:', err);
        clearInterval(tagPollTimer);
        setEditingJob(prev =>
          prev && prev.jobid === jobid
            ? { ...prev, tagsGenerating: false }
            : prev
        );
      }
    }, pollInterval);
  }

  async function handleSaveSubcomponent(content: string) {
    if (!editingSubcomponent) return;
    try {
      await api.updateSubcomponentContent(
        editingSubcomponent.jobid,
        editingSubcomponent.component,
        content
      );
      await loadJobs();
    } catch (error) {
      console.error('Failed to save subcomponent:', error);
      throw error; // Re-throw so SubcomponentEditor can handle it
    }
  }

  function handleNewResume() {
    setEditingResumeName(undefined);
    setShowResumeEditor(true);
  }

  function handleEditResume(resumename: string) {
    setEditingResumeName(resumename);
    setShowResumeEditor(true);
  }

  async function handleDeleteResume(resumename: string) {
    await api.deleteResume(resumename);
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Checkout result notification banner */}
      {checkoutNotification && (
        <div className={`flex items-center justify-between px-4 py-3 ${
          checkoutNotification.type === 'success'
            ? 'bg-green-50 text-green-800 border-b border-green-200'
            : 'bg-yellow-50 text-yellow-800 border-b border-yellow-200'
        }`}>
          <div className="flex items-center">
            {checkoutNotification.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            ) : (
              <Info className="w-5 h-5 mr-2 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{checkoutNotification.message}</span>
          </div>
          <button
            onClick={() => setCheckoutNotification(null)}
            className="p-1 hover:bg-black/5 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <Header
        onAddJobUrl={() => setShowAddJobUrlModal(true)}
        onAddJobManual={() => setShowAddJobModal(true)}
        onOpenSettings={() => setShowPreferencesModal(true)}
        selectedResume={selectedResume}
        onSelectResume={setSelectedResume}
        onEditResume={handleEditResume}
        onNewResume={handleNewResume}
        userPreferences={userPreferences}
        onPreferencesChange={(prefs) => setUserPreferences(prev => prev ? { ...prev, ...prefs } : prev)}
        resumeRefreshKey={resumeRefreshKey}
      />

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - desktop: always visible; mobile: slide-in drawer */}
        {viewMode === 'grid' && (
          <>
            {/* Desktop sidebar (hidden on mobile) */}
            <div className="hidden md:block">
              <Sidebar
                selectedPhase={selectedPhase}
                onSelectPhase={setSelectedPhase}
                phaseCounts={phaseCounts}
                onAddJobUrl={() => setShowAddJobUrlModal(true)}
                onAddJobManual={() => setShowAddJobModal(true)}
                        userPreferences={userPreferences}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />
            </div>

            {/* Mobile sidebar overlay */}
            {mobileSidebarOpen && (
              <div
                className="md:hidden fixed inset-0 bg-black/40 z-40"
                onClick={() => setMobileSidebarOpen(false)}
              />
            )}
            <div
              className={`md:hidden fixed top-0 left-0 h-full w-64 bg-white z-50 transform transition-transform duration-200 ease-in-out ${
                mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
              }`}
            >
              <Sidebar
                selectedPhase={selectedPhase}
                onSelectPhase={(phase) => {
                  setSelectedPhase(phase);
                  setMobileSidebarOpen(false);
                }}
                phaseCounts={phaseCounts}
                onAddJobUrl={() => { setShowAddJobUrlModal(true); setMobileSidebarOpen(false); }}
                onAddJobManual={() => { setShowAddJobModal(true); setMobileSidebarOpen(false); }}
                userPreferences={userPreferences}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />
            </div>
          </>
        )}

        <main className={`flex-1 overflow-y-auto ${viewMode === 'kanban' ? 'p-3 overflow-x-auto' : 'p-6'}`}>
          {/* View mode toggle + mobile sidebar button */}
          <div className="flex items-center justify-between mb-3">
            {/* Mobile sidebar toggle (only in grid mode) */}
            {viewMode === 'grid' ? (
              <button
                onClick={() => setMobileSidebarOpen(true)}
                className="md:hidden p-1.5 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                title="Show filters"
              >
                <Menu className="w-5 h-5" />
              </button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-1 ml-auto">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Grid view"
              aria-label="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'kanban'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Kanban board"
              aria-label="Kanban board"
            >
              <Columns className="w-4 h-4" />
            </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64" role="status" aria-label="Loading jobs">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 text-red-500">
              <p className="text-lg">Error loading jobs</p>
              <p className="text-sm">{error}</p>
              <button
                onClick={() => loadJobs()}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Retry
              </button>
            </div>
          ) : viewMode === 'kanban' ? (
            <KanbanBoard
              jobs={jobs}
              phaseCounts={phaseCounts}
              onRefresh={loadJobs}
              onEditSubcomponent={handleEditSubcomponent}
              onEditJob={handleEditJob}
              onGenerateResume={handleGenerateResume}
              onOptimisticPhaseChange={handleOptimisticPhaseChange}
              onJobUpdate={handleJobUpdate}
              selectedResume={selectedResume}
            />
          ) : filteredJobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Inbox className="w-12 h-12 mb-4" />
              <p className="text-lg">{searchQuery ? 'No matching jobs' : 'No jobs found'}</p>
              <p className="text-sm">{searchQuery ? 'Try a different search term' : 'Add a job to get started'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
              {filteredJobs.map((job) => (
                <JobCard
                  key={job.jobid}
                  job={job}
                  onRefresh={loadJobs}
                  onEditSubcomponent={handleEditSubcomponent}
                  onEditJob={handleEditJob}
                  onGenerateResume={handleGenerateResume}
                  onJobUpdate={handleJobUpdate}
                  selectedResume={selectedResume}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Modals */}
      <AddJobModal
        isOpen={showAddJobModal}
        onClose={() => setShowAddJobModal(false)}
        onSuccess={loadJobs}
        selectedResume={selectedResume || ''}
      />

      {showAddJobUrlModal && (
        <AddJobFromURL
          resumeid={selectedResume || ''}
          onClose={() => setShowAddJobUrlModal(false)}
          onSuccess={loadJobs}
        />
      )}

      <PreferencesModal
        isOpen={showPreferencesModal}
        onClose={() => setShowPreferencesModal(false)}
        onReplayWalkthrough={handleReplayWalkthrough}
      />

      <ResumeEditor
        isOpen={showResumeEditor}
        onClose={() => setShowResumeEditor(false)}
        onSuccess={() => {
          setShowResumeEditor(false);
          // Refresh resume list in Header and reload job data (no full page reload)
          setResumeRefreshKey(prev => prev + 1);
          loadJobs(true);
        }}
        resumename={editingResumeName}
        onDeleteResume={handleDeleteResume}
      />

      {editingSubcomponent && (
        <SubcomponentEditor
          isOpen={true}
          onClose={() => setEditingSubcomponent(null)}
          onSave={handleSaveSubcomponent}
          jobid={editingSubcomponent.jobid}
          component={editingSubcomponent.component}
          initialContent={editingSubcomponent.content}
        />
      )}

      {editingJob && (
        <JobDescriptionEditor
          jobid={editingJob.jobid}
          initialContent={editingJob.jobdesc}
          initialTags={editingJob.jobtags}
          tagsGenerating={editingJob.tagsGenerating}
          onClose={() => setEditingJob(null)}
          onSave={loadJobs}
          onRegenerateTags={handleRegenerateTags}
        />
      )}

      <TutorialModal
        isOpen={showTutorialModal}
        onClose={() => setShowTutorialModal(false)}
        onComplete={handleTutorialComplete}
      />

      <TutorialWalkthrough
        isActive={showWalkthrough}
        onComplete={handleWalkthroughComplete}
        onSkip={handleWalkthroughComplete}
      />
    </div>
  );
}
